import { useTranslation } from "react-i18next";
import { Check, Loader2, Plus } from "lucide-react";
import { Button, Badge } from "@tabipla/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@tabipla/ui/components/dialog";
import { TRIP_TYPE_TEMPLATES } from "./constants";
import { usePackingTemplates } from "./PackingContext";

export function TemplateSelectionDialog() {
  const { t } = useTranslation();
  const {
    templateItems,
    selectedTemplateItems,
    showTemplatePanel: open,
    selectedTripType: tripType,
    isBulkCreating,
    onToggleTemplateItem,
    onSelectAllTemplateItems: onSelectAll,
    onDeselectAllTemplateItems: onDeselectAll,
    onAddSelectedTemplateItems: onAddSelected,
    onCloseTemplatePanel,
  } = usePackingTemplates();

  return (
    <Dialog
      open={open && templateItems.length > 0 && tripType !== null}
      onOpenChange={(isOpen) => !isOpen && onCloseTemplatePanel()}
    >
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex max-h-[70vh] flex-col gap-0 overflow-hidden p-0 sm:flex sm:max-h-[80vh] sm:max-w-lg sm:flex-col sm:p-0"
      >
        <DialogHeader className="shrink-0 border-b px-4 pb-3 pt-4 sm:px-6 sm:pt-6">
          <div className="flex items-center gap-2">
            {tripType &&
              (() => {
                const config = TRIP_TYPE_TEMPLATES[tripType];
                const Icon = config.icon;
                return <Icon className="h-5 w-5 text-primary" />;
              })()}
            <DialogTitle>{tripType && t(TRIP_TYPE_TEMPLATES[tripType].labelKey)}</DialogTitle>
            <Badge variant="secondary">{templateItems.length}</Badge>
          </div>
          <DialogDescription className="sr-only">
            {t("packing.selectTripTypeDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b px-4 py-2">
            <span className="text-sm text-muted-foreground">
              {selectedTemplateItems.size} {t("packing.selected")}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onSelectAll}>
                {t("packing.selectAll")}
              </Button>
              <Button variant="ghost" size="sm" onClick={onDeselectAll}>
                {t("packing.deselectAll")}
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 divide-y overflow-y-auto">
            {templateItems.map((item, index) => {
              const isSelected = selectedTemplateItems.has(index);
              return (
                <div
                  key={index}
                  onClick={() => onToggleTemplateItem(index)}
                  className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/50 ${
                    isSelected ? "bg-primary/5" : ""
                  }`}
                >
                  <div
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                      isSelected
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/30"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="font-medium">{item.name}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="shrink-0 border-t p-4">
          <Button
            onClick={onAddSelected}
            disabled={selectedTemplateItems.size === 0 || isBulkCreating}
            className="w-full"
          >
            {isBulkCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("packing.adding")}
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                {t("packing.addSelected", { count: selectedTemplateItems.size })}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
